import { Check, ChevronDown, Loader2 } from 'lucide-react';
import type { CSSProperties } from 'react';
import { centralToday } from '@/lib/businessTime';
import type { AccountStatusOption } from '../config/accountStatus';
import {
  ACCOUNT_STATUS_FIELD,
  getStatusOption,
  type TabStatusField,
} from '../config/tabStatus';
import { specialOptionsForColumn } from '../config/specialOptions';
import {
  selected1099KOptions,
  TAX_1099K_OPTIONS,
} from '../config/taxFileReady';
import { isTruthyFlag } from '../format';
import type { EmployeeOption } from '../api';
import { cn } from '@/lib/utils';
import { FieldControlRow, useFieldFill } from '@/components/DetailSheet';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PAYROLL_FREQUENCY_OPTIONS } from '@/pages/SpreadsheetView/config/payrollFrequencyConfig';

// Shared editable controls for workflow fields. Every control saves the
// V1-identical payload for its column type (see updateWorkflowField):
//   Account / Sales Tax / Payroll Status → plain string ('' = no status)
//   boolean                  → true | false
//   employee_assignee        → UPPERCASE employee name, special option, or ''
//   multi_employee_assignee  → 'NAME1, NAME2' comma-joined UPPERCASE, '' = none

/** Today in CENTRAL time — {date, username} stamps are shared business data,
 *  so a remote viewer must never stamp their own calendar day */
export function todayLocalISO(): string {
  return centralToday();
}

/** The YYYY-MM-DD inside a date_with_user value (string or {date, username}) */
export function dateOf(value: unknown): string | null {
  if (typeof value === 'string') return value.slice(0, 10) || null;
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const d = (value as { date?: unknown }).date;
    if (typeof d === 'string' && d) return d.slice(0, 10);
  }
  return null;
}

export function Spinner() {
  return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />;
}

/** The canonical interactive-field pill: neutral bg-muted gray, overridden
 *  inline by statusTintStyle when the row has an Account Status */
export const PILL_SELECT_CLASS =
  'max-w-[11rem] truncate rounded-full border-0 bg-muted py-0.5 pl-2 pr-6 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

/** Added to a select / dropdown trigger inside a detail-panel field row so
 *  it fills the column (see useFieldFill) */
export const FILL_CONTROL_CLASS = 'w-full max-w-none min-w-0 flex-1';

/** Single (non-split) action buttons sharing the same gray theme */
export const ACTION_BUTTON_CLASS =
  'rounded-md border border-transparent bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-ring';

/**
 * Inline tint an editor in the row's Account Status colors, exactly like the
 * StatusSelect pill. Undefined status (row has no status, or a mobile caller
 * that keeps the neutral look) → no style, the editor's own classes apply.
 */
export function statusTintStyle(
  status: AccountStatusOption | undefined
): CSSProperties | undefined {
  if (!status) return undefined;
  return {
    backgroundColor: status.highlightColor,
    color: status.rowTextColor === 'inherit' ? undefined : status.rowTextColor,
    fontStyle: status.italic ? 'italic' : undefined,
  };
}

/**
 * Segment classes for the table's split buttons (date stamps, sales-tax
 * Complete): neutral bg-muted gray; the caller's statusTintStyle(status)
 * inline style overrides bg/color on both segments when the row is tinted.
 * The param is kept so existing callers (ExtensionsV2, LlcFilingV2) compile
 * unchanged.
 */
export function splitButtonClasses(_tinted: boolean): {
  main: string;
  chevron: string;
} {
  const focus = 'focus:outline-none focus:ring-2 focus:ring-ring';
  return {
    main: cn(
      'rounded-l-md border border-transparent bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground hover:brightness-95',
      focus
    ),
    chevron: cn(
      'self-stretch rounded-r-md border border-transparent border-l-black/10 bg-muted px-0.5 text-foreground hover:brightness-95',
      focus
    ),
  };
}

/**
 * Sizing for the cell dropdown menus (Bookkeeper checklist, date options,
 * QB Process, …): readable text + full tap targets on touch screens,
 * compact text-xs on desktop where the cells are dense.
 */
export const MENU_ITEM_CLASS = 'py-2.5 text-sm sm:py-1.5 sm:text-xs';

interface StatusSelectProps {
  value: unknown;
  saving: boolean;
  onSave: (value: string) => void;
  /** Which status column and options (default: Account Status) */
  field?: TabStatusField;
  /** Tint the pill in the selected status's colors (the table); false
   *  keeps the neutral gray pill (the detail sheet) */
  tinted?: boolean;
}

/** A tab's status dropdown, tinted in the current status's colors;
 *  "No Status" renders the neutral gray pill like every other editor. */
export function StatusSelect({
  value,
  saving,
  onSave,
  field = ACCOUNT_STATUS_FIELD,
  tinted = true,
}: StatusSelectProps) {
  const fill = useFieldFill();
  const selected = getStatusOption(field, value);
  const current = selected ?? field.options[0];
  return (
    <FieldControlRow>
      <select
        value={current.value}
        disabled={saving}
        onChange={e => onSave(e.target.value)}
        aria-label={field.column}
        className={cn(
          PILL_SELECT_CLASS,
          'max-w-full',
          fill && FILL_CONTROL_CLASS
        )}
        style={tinted ? statusTintStyle(selected) : undefined}
      >
        {field.options.map(o => (
          <option key={o.value || 'none'} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {saving && <Spinner />}
    </FieldControlRow>
  );
}

interface BooleanToggleProps {
  value: unknown;
  saving: boolean;
  onSave: (value: boolean) => void;
  /** Row's Account Status; tints the toggle in the status colors */
  status?: AccountStatusOption;
}

/** Compact ✓/— toggle; a click saves the opposite state (V1 stores booleans). */
export function BooleanToggle({
  value,
  saving,
  onSave,
  status,
}: BooleanToggleProps) {
  const truthy = isTruthyFlag(value);
  const tint = statusTintStyle(status);
  if (saving) return <Spinner />;
  // Glyph + weight carry the state, dimmed when unset; the tint style (if
  // any) overrides the gray bg/color inline
  const stateClass = cn(
    'border-transparent bg-muted text-foreground hover:brightness-95',
    truthy ? 'font-semibold' : 'opacity-60'
  );
  return (
    <button
      onClick={() => onSave(!truthy)}
      aria-label={truthy ? 'Mark no' : 'Mark yes'}
      aria-pressed={truthy}
      className={cn(
        'inline-flex h-6 w-9 items-center justify-center rounded-md border text-sm transition-colors',
        stateClass
      )}
      style={tint}
    >
      {truthy ? <Check className="h-3.5 w-3.5" /> : '—'}
    </button>
  );
}

interface AssigneeSelectProps {
  columnName: string;
  value: unknown;
  employees: EmployeeOption[];
  saving: boolean;
  onSave: (value: string) => void;
  /** Row's Account Status; tints the pill in the status colors */
  status?: AccountStatusOption;
}

/**
 * Single-assignee dropdown: Unassigned, the column's special options
 * (NA, No Sales Tax, …), then employees. Values are UPPERCASE names.
 */
export function AssigneeSelect({
  columnName,
  value,
  employees,
  saving,
  onSave,
  status,
}: AssigneeSelectProps) {
  const fill = useFieldFill();
  const raw = typeof value === 'string' ? value.trim() : '';
  const specials = specialOptionsForColumn(columnName);
  const known = new Set([
    '',
    ...specials,
    ...employees.map(e => e.name.toUpperCase()),
  ]);
  return (
    <FieldControlRow>
      <select
        value={raw}
        disabled={saving}
        onChange={e => onSave(e.target.value)}
        aria-label={columnName}
        className={cn(PILL_SELECT_CLASS, fill && FILL_CONTROL_CLASS)}
        style={statusTintStyle(status)}
      >
        <option value="">Unassigned</option>
        {specials.map(name => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
        {employees.map(e => (
          <option key={e.id} value={e.name.toUpperCase()}>
            {e.name}
          </option>
        ))}
        {/* Legacy/unknown stored value: keep it selectable so it displays */}
        {raw && !known.has(raw) && <option value={raw}>{raw}</option>}
      </select>
      {saving && <Spinner />}
    </FieldControlRow>
  );
}

interface MultiAssigneeChipsProps {
  value: unknown;
  employees: EmployeeOption[];
  saving: boolean;
  onSave: (value: string) => void;
  /** Row's Account Status; tints the pill in the status colors */
  status?: AccountStatusOption;
}

/** Parse a multi-assignee value (string[] or comma string) into UPPERCASE names */
export function parseAssigneeNames(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
      .map(v => v.trim().toUpperCase());
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(',')
      .map(v => v.trim().toUpperCase())
      .filter(Boolean);
  }
  return [];
}

/**
 * Multi-assignee dropdown (Bookkeeper): an Account-Status-style pill;
 * clicking opens a checklist of employees. Each toggle saves immediately as
 * a comma-joined UPPERCASE string (V1's shape). Legacy names not in the
 * employee list stay listed so they can be seen and removed.
 *
 * Radix menu (portaled to body) rather than an in-table absolute popover:
 * the row hover filter creates a stacking context, so an inline popover gets
 * painted UNDER the sticky first-column cells of the rows below it no matter
 * its z-index.
 */
export function MultiAssigneeDropdown({
  value,
  employees,
  saving,
  onSave,
  status,
}: MultiAssigneeChipsProps) {
  const tint = statusTintStyle(status);
  const fill = useFieldFill();
  const selected = new Set(parseAssigneeNames(value));
  const extraSelected = [...selected].filter(
    name => !employees.some(e => e.name.toUpperCase() === name)
  );

  const toggle = (name: string) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    onSave([...next].join(', '));
  };

  const properName = (upper: string) =>
    employees.find(e => e.name.toUpperCase() === upper)?.name ?? upper;

  // Selecting must NOT close the menu — multiple bookkeepers get toggled in
  // one sitting (matches the previous popover behavior)
  const optionRow = (key: string, label: string, upper: string) => (
    <DropdownMenuCheckboxItem
      key={key}
      checked={selected.has(upper)}
      disabled={saving}
      onSelect={e => e.preventDefault()}
      onCheckedChange={() => toggle(upper)}
      className={cn(
        MENU_ITEM_CLASS,
        'text-foreground',
        selected.has(upper) && 'font-medium'
      )}
    >
      <span className="truncate">{label}</span>
    </DropdownMenuCheckboxItem>
  );

  return (
    <FieldControlRow>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            disabled={saving}
            className={cn(
              'inline-flex max-w-[11rem] items-center gap-1 rounded-full border-0 bg-muted py-0.5 pl-2 pr-2 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
              fill && cn(FILL_CONTROL_CLASS, 'justify-between')
            )}
            style={tint}
          >
            {selected.size === 0 ? (
              <span className={tint ? 'opacity-70' : 'text-muted-foreground'}>
                Unassigned
              </span>
            ) : (
              <span className="truncate">
                {[...selected].map(properName).join(' / ')}
              </span>
            )}
            <ChevronDown
              className={cn(
                'h-3 w-3',
                tint ? 'opacity-70' : 'text-muted-foreground'
              )}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          onCloseAutoFocus={e => e.preventDefault()}
          className="max-h-72 w-56 overflow-y-auto sm:w-48"
        >
          {employees.map(e => optionRow(e.id, e.name, e.name.toUpperCase()))}
          {extraSelected.map(name => optionRow(name, name, name))}
        </DropdownMenuContent>
      </DropdownMenu>
      {saving && <Spinner />}
    </FieldControlRow>
  );
}

interface Tax1099KDropdownProps {
  value: unknown;
  saving: boolean;
  /** Saves the full {option: boolean} object (clients.tax_1099k jsonb) */
  onSave: (value: Record<string, boolean>) => void;
  /** Row's Account Status; tints the pill in the status colors */
  status?: AccountStatusOption;
}

/**
 * The Tax File Ready 1099K multi-toggle: an Account-Status-style pill;
 * clicking opens a checklist of the 1099K flag + delivery platforms
 * (TAX_1099K_OPTIONS). Each toggle saves immediately as a {option: boolean}
 * object — clients.tax_1099k's jsonb shape (sql/workflow-v2/011); only
 * toggled-on options are stored, a missing key means off. Legacy keys not in
 * the option list stay listed so they can be seen and removed.
 */
export function Tax1099KDropdown({
  value,
  saving,
  onSave,
  status,
}: Tax1099KDropdownProps) {
  const tint = statusTintStyle(status);
  const fill = useFieldFill();
  const selected = new Set(selected1099KOptions(value));
  const extraSelected = [...selected].filter(
    option =>
      !TAX_1099K_OPTIONS.includes(option as (typeof TAX_1099K_OPTIONS)[number])
  );

  const toggle = (option: string) => {
    const next = new Set(selected);
    if (next.has(option)) next.delete(option);
    else next.add(option);
    onSave(Object.fromEntries([...next].map(key => [key, true])));
  };

  // Selecting must NOT close the menu — several platforms get toggled in one
  // sitting (same behavior as the Bookkeeper checklist)
  const optionRow = (option: string) => (
    <DropdownMenuCheckboxItem
      key={option}
      checked={selected.has(option)}
      disabled={saving}
      onSelect={e => e.preventDefault()}
      onCheckedChange={() => toggle(option)}
      className={cn(
        MENU_ITEM_CLASS,
        'text-foreground',
        selected.has(option) && 'font-medium'
      )}
    >
      <span className="truncate">{option}</span>
    </DropdownMenuCheckboxItem>
  );

  return (
    <FieldControlRow>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={saving}
            aria-label="1099K"
            className={cn(
              'inline-flex max-w-[11rem] items-center gap-1 rounded-full border-0 bg-muted py-0.5 pl-2 pr-2 text-xs font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-ring',
              fill && cn(FILL_CONTROL_CLASS, 'justify-between')
            )}
            style={tint}
          >
            {selected.size === 0 ? (
              <span className={tint ? 'opacity-70' : 'text-muted-foreground'}>
                None
              </span>
            ) : (
              <span className="truncate">{[...selected].join(' / ')}</span>
            )}
            <ChevronDown
              className={cn(
                'h-3 w-3',
                tint ? 'opacity-70' : 'text-muted-foreground'
              )}
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          onCloseAutoFocus={e => e.preventDefault()}
          className="max-h-72 w-56 overflow-y-auto sm:w-48"
        >
          {TAX_1099K_OPTIONS.map(option => optionRow(option))}
          {extraSelected.map(option => optionRow(option))}
        </DropdownMenuContent>
      </DropdownMenu>
      {saving && <Spinner />}
    </FieldControlRow>
  );
}

interface QbProcessSplitButtonProps {
  value: unknown;
  saving: boolean;
  onSave: (value: string) => void;
  /** Row's Account Status; tints the button in the status colors */
  status?: AccountStatusOption;
}

/**
 * Table-cell split button for QB Process: current value (or "Set…") plus an
 * Online / Manual / Clear menu. Saves the same V1 payloads as the detail
 * sheet's QbProcessToggle ('' | 'Manual' | 'Online') so the
 * clients.qb_process routing stays intact. Legacy values display as stored.
 */
export function QbProcessSplitButton({
  value,
  saving,
  onSave,
  status,
}: QbProcessSplitButtonProps) {
  const raw = typeof value === 'string' ? value.trim() : '';
  const tint = statusTintStyle(status);
  if (saving) return <Spinner />;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          aria-label="QB Process"
          className="inline-flex max-w-full items-center rounded-md border border-transparent bg-muted text-xs font-medium text-foreground hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-ring"
          style={tint}
        >
          <span
            className={cn(
              'truncate px-1.5 py-0.5',
              !raw && (tint ? 'opacity-70' : 'text-muted-foreground')
            )}
          >
            {raw || 'Set…'}
          </span>
          <span className="inline-flex items-center self-stretch border-l border-black/10 px-0.5 opacity-70">
            <ChevronDown className="h-3 w-3" />
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        onCloseAutoFocus={e => e.preventDefault()}
      >
        <DropdownMenuItem
          className={MENU_ITEM_CLASS}
          onSelect={() => onSave('Online')}
        >
          Online
        </DropdownMenuItem>
        <DropdownMenuItem
          className={MENU_ITEM_CLASS}
          onSelect={() => onSave('Manual')}
        >
          Manual
        </DropdownMenuItem>
        <DropdownMenuItem
          className={MENU_ITEM_CLASS}
          disabled={!raw}
          onSelect={() => onSave('')}
        >
          Clear
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface PayrollFrequencySelectProps {
  value: unknown;
  saving: boolean;
  onSave: (value: string) => void;
  /** Row's Account Status; tints the pill in the status colors */
  status?: AccountStatusOption;
}

/**
 * Payroll Frequency dropdown with V1's exact option keys
 * (Monthly/SemiMonthly/BiWeekly/Weekly, '' = not set) shown as labels.
 */
export function PayrollFrequencySelect({
  value,
  saving,
  onSave,
  status,
}: PayrollFrequencySelectProps) {
  const raw = typeof value === 'string' ? value.trim() : '';
  const known = PAYROLL_FREQUENCY_OPTIONS.some(o => o.value === raw);
  const fill = useFieldFill();
  return (
    <FieldControlRow>
      <select
        value={raw}
        disabled={saving}
        onChange={e => onSave(e.target.value)}
        aria-label="Payroll Frequency"
        className={cn(PILL_SELECT_CLASS, fill && FILL_CONTROL_CLASS)}
        style={statusTintStyle(status)}
      >
        <option value="">Not set</option>
        {PAYROLL_FREQUENCY_OPTIONS.map(o => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        {/* Legacy/unknown stored value: keep it selectable so it displays */}
        {raw && !known && <option value={raw}>{raw}</option>}
      </select>
      {saving && <Spinner />}
    </FieldControlRow>
  );
}
